import { ordinal } from "@/utils/deadlineDay";
import type { THomepageColumnKey, THomepageRow } from "@/store";

/** Day-of-month from a "YYYY-MM-DD" string, parsed without any Date/timezone
 * conversion — these are calendar dates, not instants. */
function dayOfMonth(isoDate: string): number {
    const day = isoDate.split("-")[2];
    return day ? Number(day) : Number.NaN;
}

/** Whole-day difference between two "YYYY-MM-DD" calendar dates (b - a),
 * computed via UTC noon anchors so DST transitions can never shift the
 * result by a day. */
function daysBetween(aIso: string, bIso: string): number {
    const a = Date.UTC(...(parseYmd(aIso) as [number, number, number]));
    const b = Date.UTC(...(parseYmd(bIso) as [number, number, number]));
    return Math.round((b - a) / 86_400_000);
}

function parseYmd(iso: string): [number, number, number] {
    const [y, m, d] = iso.split("-").map(Number);
    return [y ?? 0, (m ?? 1) - 1, d ?? 1];
}

function dueLabel(dueDate: string): string {
    return `Due ${ordinal(dayOfMonth(dueDate))}`;
}

export type THomepageRowCopy = {
    /** SP columns: "N failed upload(s)", null when there were no failures.
     * Accountant columns: the status line ("Fix 3 errors · Submitted 5 days ago").
     *
     * Rendered in the card's normal muted text whatever it says: most
     * submissions have errors to fix, so tinting every one of them red made
     * the board read as one undifferentiated wall of warnings (§5.6). */
    line1: string | null;
    line2: string | null;
};

function days(n: number): string {
    return `${n} day${n === 1 ? "" : "s"}`;
}

/**
 * Per-column row copy for both views (design doc §7.1's copy table).
 *
 * Accountant "Ready for you" rows lead with the server's next-action label
 * (§5.4) where there is one — "Fix 3 errors", "Balance" — falling back to the
 * table's generic "Review needed" for a status with no defined action. The
 * label is deliberately not re-derived here: the server owns the mapping,
 * including the error count it interpolates.
 */
export function formatHomepageRowCopy(
    row: THomepageRow,
    columnKey: THomepageColumnKey,
): THomepageRowCopy {
    const failedLine =
        row.failedAttempts > 0
            ? `${row.failedAttempts} failed upload${row.failedAttempts === 1 ? "" : "s"}`
            : null;

    if (columnKey === "not_yet_due" && row.dueDate !== null && row.daysUntilDue !== null) {
        const n = row.daysUntilDue;
        const line2 = `Submission expected in ${n} day${n === 1 ? "" : "s"} · ${dueLabel(row.dueDate)}`;
        return { line1: failedLine, line2 };
    }

    if (columnKey === "upload_now" && row.dueDate !== null && row.daysUntilDue !== null) {
        const overdueBy = -row.daysUntilDue;
        const line2 =
            overdueBy <= 0
                ? `Submission due today · ${dueLabel(row.dueDate)}`
                : `Submission ${overdueBy} day${overdueBy === 1 ? "" : "s"} late · ${dueLabel(row.dueDate)}`;
        return { line1: failedLine, line2 };
    }

    if (columnKey === "submitted" && row.dueDate !== null) {
        if (row.submittedOnTime === false && row.submittedAt !== null) {
            const daysLate = daysBetween(row.dueDate, row.submittedAt.slice(0, 10));
            const line2 = `Submitted ${daysLate} day${daysLate === 1 ? "" : "s"} late · ${dueLabel(row.dueDate)}`;
            return { line1: failedLine, line2 };
        }
        return {
            line1: failedLine,
            line2: `Submitted (on time) · ${dueLabel(row.dueDate)}`,
        };
    }

    if (columnKey === "waiting_on_service_provider") {
        // An outstanding OTB is not the service provider's to send: it is
        // uploaded during deal setup and committed by a finance user. Naming
        // the wrong party here would send an accountant chasing a file the
        // service provider cannot produce. It is also the one row that can
        // arrive without a due date (a deal with no deadline day configured),
        // so its copy must not depend on having one.
        if (row.dueDate !== null) {
            return {
                line1: "Awaiting files",
                line2: `Waiting for files · ${dueLabel(row.dueDate)}`,
            };
        }
    }

    if (columnKey === "ready_for_you" && row.fileType === "open_trial_balance" && !row.fileId) {
        // An opening balance nobody has uploaded yet. It is not "in review" and
        // has nothing submitted to count days from, so the review copy below
        // cannot describe it — and a deal with no deadline day still gets a
        // row, so the date is optional.
        return {
            line1: "Opening balance not uploaded",
            line2:
                row.dueDate !== null
                    ? `Blocks all uploads on this deal · ${dueLabel(row.dueDate)}`
                    : "Blocks all uploads on this deal",
        };
    }

    if (columnKey === "ready_for_you" && row.dueDate !== null && row.daysUntilDue !== null) {
        const action = row.nextActionLabel ?? "Review needed";
        const line1 =
            row.daysSinceSubmitted !== null
                ? `${action} · Submitted ${days(row.daysSinceSubmitted)} ago`
                : action;
        // One deadline drives both the submission and the review (decision log
        // §11), so "days remaining" counts down to the same due date and flips
        // to "late" once it passes.
        const line2 =
            row.daysUntilDue >= 0
                ? `In review (${days(row.daysUntilDue)} remaining) · ${dueLabel(row.dueDate)}`
                : `${days(-row.daysUntilDue)} late · ${dueLabel(row.dueDate)}`;
        return { line1, line2 };
    }

    if (columnKey === "submitted_to_erp" && row.dueDate !== null) {
        const line1 = "Submission and Review complete";
        if (row.completedAt === null) {
            return { line1, line2: dueLabel(row.dueDate) };
        }
        const delta = daysBetween(row.dueDate, row.completedAt.slice(0, 10));
        const line2 =
            delta === 0
                ? `Completed on the due date · ${dueLabel(row.dueDate)}`
                : `Completed ${days(Math.abs(delta))} ${delta < 0 ? "early" : "late"} · ${dueLabel(row.dueDate)}`;
        return { line1, line2 };
    }

    // Any row with no due date — the board doesn't currently emit one (§10's
    // "Nothing due this period" case is filtered server-side), so this is the
    // defensive tail, not a rendered state.
    return {
        line1: failedLine,
        line2: row.dueDate !== null ? dueLabel(row.dueDate) : null,
    };
}
